import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { HERO_IMAGE, PROJECT } from "../../data/project";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full h-[100svh] min-h-[680px] overflow-hidden"
    >
      <motion.div
        initial={{ scale: 1.12 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.6, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0"
      >
        <img
          src={HERO_IMAGE}
          alt="Hudayriyat Golf Estates"
          className="w-full h-full object-cover object-[center_30%] md:object-center"
        />
        {/* Soft gradient — fully opaque at bottom to mask image's lower watermark, lighter at top */}
        <div className="absolute inset-x-0 bottom-0 h-3/4 md:h-2/3 bg-gradient-to-t from-[#1A3626] via-[#1A3626]/55 md:via-[#1A3626]/25 to-transparent" />
      </motion.div>

      <div className="absolute top-24 left-6 md:left-12 z-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="text-[#F4F2EC] text-[10px] md:text-[11px] tracking-[0.4em] uppercase font-medium drop-shadow-md"
        >
          Modon · Hudayriyat Island · Abu Dhabi
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 pb-14 md:pb-20">
          <div className="grid grid-cols-12 gap-6 items-end">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 md:col-span-8"
            >
              <p className="hidden md:block max-w-xl text-[#F4F2EC]/90 text-base md:text-lg font-light leading-relaxed drop-shadow">
                {PROJECT.story}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8 }}
              className="col-span-12 md:col-span-4 flex flex-col gap-4 md:items-end"
            >
              <button
                data-testid="hero-cta-register"
                onClick={() => scrollTo("contact")}
                className="hge-btn-light"
              >
                Register Interest
                <ArrowDownRight className="w-4 h-4" />
              </button>
              <button
                data-testid="hero-cta-collection"
                onClick={() => scrollTo("collection")}
                className="text-[#F4F2EC]/80 hover:text-[#F4F2EC] text-[12px] tracking-[0.22em] uppercase transition-colors flex items-center gap-2"
              >
                Explore the Collection
                <ArrowDownRight className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="mt-12 pt-6 border-t border-[#F4F2EC]/15 grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            <Stat label="Handover" value="Mar 2030" />
            <Stat label="Payment Split" value="40 / 60" />
            <Stat label="Starting From" value="AED 4.25M" />
            <Stat label="Projected ROI" value="114 – 118%" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div data-testid={`hero-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#F4F2EC]/55 font-medium">
        {label}
      </div>
      <div className="mt-2 font-serif text-2xl md:text-3xl text-[#F4F2EC]">
        {value}
      </div>
    </div>
  );
}
